import { component } from "picoapp";
import {
  PAGE_CAROUSEL_OPTIONS,
  PAGINATION_ACTIVE_CLASS,
  PAGINATION_INACTIVE_CLASS,
  SLIDE_FADE_CLASS,
} from "../tbsp-carousel--constants";
import { type Context } from "@tablescape/state";
import {
  EventInterface,
  type Components,
  type Splide,
  type Options,
} from "@splidejs/splide";

export function VideoAutoPlay(
  Splide: Splide,
  Components: Components,
  options: Options
) {
  const { on } = EventInterface(Splide);
  const { Controller, Elements, Slides } = Components;
  const autoPlayDuration = options.interval || 5;

  function autoPlayVideo() {
    const currentIndex = Controller.getIndex();
    const currentSlide = Slides.getAt(currentIndex);
    const currentVideo =
      currentSlide?.slide.querySelector<HTMLVideoElement>("video");
    if (!currentVideo) return;

    currentVideo.play();
  }

  function setVideoAttributes() {
    const { root } = Elements;
    const videos = root.querySelectorAll<HTMLVideoElement>("video");

    videos.forEach((video) => {
      video.preload = "none";
      video.muted = true;
      video.autoplay = false;
      video.playsInline = true;

      const goToNextVideo = () => {
        video.pause();
        Controller.go("+1");
      };

      const setVideoIntervals = () => {
        if (video.currentTime * 1000 >= autoPlayDuration) {
          if (!video.paused) {
            goToNextVideo();
          }
        }
      };

      video.addEventListener("ended", goToNextVideo);
      video.addEventListener("timeupdate", setVideoIntervals);
    });
  }

  function mount() {
    let activeCount = 0;
    setVideoAttributes();
    on("intersection:in", () => {
      autoPlayVideo();
    });

    on("intersection:out", () => {
      const currentIndex = Controller.getIndex();
      const currentSlide = Slides.getAt(currentIndex);
      const video =
        currentSlide?.slide.querySelector<HTMLVideoElement>("video");
      video?.pause();
    });

    on("active", () => {
      if (activeCount > 0) {
        autoPlayVideo();
      } else {
        activeCount++;
      }
    });

    on("inactive", ({ slide }) => {
      const video = slide.querySelector<HTMLVideoElement>("video");
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
  }

  return {
    mount,
  };
}

export const paginatedCarousel = component(
  async (node: HTMLElement, context: Context) => {
    const isVideoEnabled = node.dataset.videoEnabled === "true";
    const isPausingEnabled = node.dataset.pausingEnabled === "true";
    const isFadeEnabled = node.dataset.fadeInactive === "true";
    const isLoopingEnabled = node.dataset.loopingEnabled === "true";
    const isPaginationEnabled = node.dataset.paginationEnabled === "true";
    const isVideoAutoPlayEnabled = node.dataset.videoAutoplayEnabled === "true";
    const rootMargin = node.dataset.rootMargin ?? "200px";
    const threshold = node.dataset.threshold
      ? parseInt(node.dataset.threshold)
      : 0;
    const persistIntersection = node.dataset.persistIntersection === "true";
    const slideGap = node.dataset.slideGap;
    const mdSlideGap = node.dataset.mdSlideGap;

    const [{ default: Splide }, { Intersection }] = await Promise.all([
      import("@splidejs/splide"),
      import("@splidejs/splide-extension-intersection"),
      import("@splidejs/splide/css/core"),
    ]);

    const derivedGap = slideGap
      ? parseInt(slideGap)
      : PAGE_CAROUSEL_OPTIONS.gap;

    const splide = new Splide(node, {
      ...PAGE_CAROUSEL_OPTIONS,
      type: isLoopingEnabled ? "loop" : PAGE_CAROUSEL_OPTIONS.type,
      pagination: isPaginationEnabled,
      gap: derivedGap,

      breakpoints: {
        ...PAGE_CAROUSEL_OPTIONS.breakpoints,
        768: {
          ...PAGE_CAROUSEL_OPTIONS.breakpoints[768],
          gap: mdSlideGap ? parseInt(mdSlideGap) : derivedGap,
        },
      },

      intersection: {
        rootMargin,
        threshold,
        once: !persistIntersection,
      },
    });

    if (isVideoEnabled) {
      splide.on("intersection:in", function () {
        splide.on("active", ({ slide }) => {
          const video = slide.querySelector("video");

          if (video && isPausingEnabled) {
            video.play().catch();
          }
        });

        splide.on("inactive", ({ slide }) => {
          const video = slide.querySelector("video");

          if (video && isPausingEnabled) {
            video.muted = true;
            video.pause();
          }
        });

        if (!isVideoAutoPlayEnabled) {
          node.querySelectorAll("video").forEach((video) => {
            video.preload = "auto";
          });
        }
      });

      context.on("tbsp:button-clicked", ({ action, target }) => {
        // Disable autoplay and video slides when modal is toggled
        if (action === "toggle-modal") {
          const { Controller, Slides, VideoAutoPlay } = splide.Components;
          if (VideoAutoPlay) {
            const currentIndex = Controller.getIndex();
            const activeSlide = Slides.getAt(currentIndex);
            const video =
              activeSlide?.slide.querySelector<HTMLVideoElement>("video");
            if (!video) return;
            video.pause();
            video.currentTime = 0;
          }

          const modal = document.querySelector(
            `[role='dialog'][data-tbsp-target='${target}']`
          );

          if (modal) {
            const modalVideo = modal.querySelector("video");
            if (modalVideo) {
              const resumeAutoPlayVideo = () => {
                const currentIndex = Controller.getIndex();
                const activeSlide = Slides.getAt(currentIndex);
                const video =
                  activeSlide?.slide.querySelector<HTMLVideoElement>("video");
                video?.play();
                modalVideo.removeEventListener("pause", resumeAutoPlayVideo);
              };
              modalVideo.addEventListener("pause", resumeAutoPlayVideo);
            }
          }
        }
      });
    }

    if (isFadeEnabled) {
      node
        .querySelectorAll(".splide__slide")
        .forEach((slide) => slide.classList.add(SLIDE_FADE_CLASS));

      splide.on("active", ({ slide }) => {
        slide.classList.remove(SLIDE_FADE_CLASS);
      });

      splide.on("inactive", ({ slide }) => {
        slide.classList.add(SLIDE_FADE_CLASS);
      });

      splide.on("click", ({ index }) => {
        splide.go(index);
      });
    }

    if (isPaginationEnabled) {
      splide.on("pagination:updated", (_, prev, curr) => {
        prev.button.classList.add(PAGINATION_INACTIVE_CLASS);
        prev.button.classList.remove(PAGINATION_ACTIVE_CLASS);

        curr.button.classList.add(PAGINATION_ACTIVE_CLASS);
        curr.button.classList.remove(PAGINATION_INACTIVE_CLASS);
      });
    }

    if (isVideoAutoPlayEnabled) {
      splide.mount({
        Intersection,
        VideoAutoPlay,
      });
    } else {
      splide.mount({
        Intersection,
      });
    }
  }
);
